import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import useExtendedState from '../../hooks/useExtendedState';
import TextField from '../fields/TextField';
import NumberField from '../fields/NumberField';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import {
    CURRENCY_CODE_MAX_LENGTH,
    CURRENCY_NAME_MAX_LENGTH,
    currencyCodeValidator,
    currencyExchangeRateValidator,
    currencyNameValidator,
} from '../../validators/currencies';
import useMultiValidator from '../../hooks/useMultiValidator';
import { ALL_CURRENCIES_QUERY_NAME, CREATE_CURRENCY } from '../../graphql/currencies';
import SaveButton from '../buttons/SaveButton';
import Constants from '../../utils/Constants';
import ExpandedListTab from '../ExpandedListTab';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';

CurrencyCreateTab.propTypes = {
    hideHandler: PropTypes.func.isRequired,
};

function CurrencyCreateTab(props) {
    const { hideHandler } = props;
    const { t } = useTranslation();
    const nameState = useExtendedState('');
    const codeState = useExtendedState('');
    const exchangeRateState = useExtendedState(Constants.DEFAULT_EXCHANGE_RATE);
    const { validators, isValid } = useMultiValidator(false, {
        name: compositeValidator(currencyNameValidator(t), wafValidator(t)),
        code: compositeValidator(currencyCodeValidator(t), wafValidator(t)),
        exchangeRate: compositeValidator(currencyExchangeRateValidator(t), wafValidator(t)),
    }, {
        exchangeRate: true,
    });

    const [isDisabled, setIsDisabled] = useState(true);
    isValid()
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));

    const extendedMutation = useExtendedMutation(CREATE_CURRENCY, {
        variables: {
            code: codeState.value,
            name: nameState.value,
            exchangeRate: parseFloat(exchangeRateState.value),
        },
        successCallback: hideHandler,
        successMessageOptions: { key: 'currencies.notification.currency-create-success', dataProperty: 'createCurrency' },
        failureMessageOptions: { key: 'currencies.notification.currency-create-failure', params: { name: nameState.value, code: codeState.value } },
        refetchQueries: [ALL_CURRENCIES_QUERY_NAME],
    });
    return (
        <ExpandedListTab
            className="currency-create-tab"
            title={t('currencies.currency-detail')}
        >
            <div className="rich-list-row-expanded--fields">
                <TextField
                    label={t('currencies.code')}
                    state={codeState}
                    className="currency-create-tab--fields--code"
                    validator={validators.code}
                    placeholder={t('currencies.placeholder.code')}
                    maxLength={CURRENCY_CODE_MAX_LENGTH}
                    isRequired
                />
                <TextField
                    label={t('currencies.name')}
                    state={nameState}
                    className="currency-create-tab--fields--name wide"
                    validator={validators.name}
                    placeholder={t('currencies.placeholder.name')}
                    maxLength={CURRENCY_NAME_MAX_LENGTH}
                    isRequired
                />
                <NumberField
                    label={t('currencies.exchange-rate')}
                    state={exchangeRateState}
                    fractionDigits={4}
                    step={Constants.DEFAULT_EXVHANGE_RATE_STEP}
                    className="currency-create-tab--fields--exchange-rate"
                    validator={validators.exchangeRate}
                    postfix={t('currencies.exchange-rate-to-euro')}
                    isRequired
                />
            </div>
            <div className="rich-list-row-expanded--buttons">
                <SaveButton extendedMutation={extendedMutation} disabled={isDisabled} confirmationMessage={t('currencies.confirm-create-currency', { name: nameState.value, code: codeState.value })} />
            </div>
        </ExpandedListTab>
    );
}

export default CurrencyCreateTab;
